import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { fmtBulan, fmtPersen } from "@/lib/hpp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Copy, Trash2, GitCompare, Calculator, Search, CheckSquare, Square } from "lucide-react";

export default function HppHome() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const load = () => api.get("/hpp/simulations").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 3 ? (toast.error("Maksimal 3 simulasi untuk dibandingkan"), s) : [...s, id]));
  const dup = async (it) => { try { await api.post(`/hpp/simulations/${it.id}/duplicate`); toast.success("Simulasi diduplikat"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (it) => {
    if (!window.confirm(`Hapus simulasi "${it.nama}"?`)) return;
    try { await api.delete(`/hpp/simulations/${it.id}`); setSel((s) => s.filter((x) => x !== it.id)); toast.success("Simulasi dihapus"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = (items || []).filter((it) => !q || `${it.nama} ${it.kategori_nama} ${it.deskripsi}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 md:p-8 space-y-4">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-slate-900">Simulasi Saya</h1>
          <p className="text-xs md:text-sm text-slate-500">Simpan banyak skenario bisnis, buka lagi kapan saja, dan bandingkan hingga 3 simulasi.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={sel.length < 2} onClick={() => nav(`/hpp/bandingkan?ids=${sel.join(",")}`)} className="rounded-sm h-11 md:h-10 flex-1 md:flex-none" data-testid="hpp-compare-btn">
            <GitCompare size={16} className="mr-1.5" /> Bandingkan ({sel.length})
          </Button>
          <Button onClick={() => nav("/hpp/baru")} className="brand-btn rounded-sm h-11 md:h-10 flex-1 md:flex-none" data-testid="hpp-new-btn"><Plus size={16} className="mr-1.5" /> Simulasi Baru</Button>
        </div>
      </div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari simulasi..." className="pl-9 h-11 md:h-10 bg-white" data-testid="hpp-search" />
      </div>
      {items === null && <div className="text-sm text-slate-400 py-10 text-center">Memuat...</div>}
      {items && shown.length === 0 && (
        <div className="card-flat p-10 text-center space-y-3" data-testid="hpp-empty">
          <Calculator size={36} className="mx-auto text-slate-300" />
          <div className="text-slate-600 font-medium">Belum ada simulasi</div>
          <p className="text-xs text-slate-400">Mulai hitung HPP, harga jual, dan BEP bisnis Anda.</p>
          <Button onClick={() => nav("/hpp/baru")} className="brand-btn rounded-sm"><Plus size={16} className="mr-1.5" /> Buat Simulasi Pertama</Button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
        {shown.map((it) => {
          const h = it.hasil || {};
          const on = sel.includes(it.id);
          return (
            <div key={it.id} className={`card-flat p-4 md:p-5 flex flex-col gap-3 transition-shadow hover:shadow-md ${on ? "ring-2 ring-[color:var(--brand)]" : ""}`} data-testid={`hpp-card-${it.id}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(it.id)} className="mt-0.5 text-slate-400 hover:text-[color:var(--brand)] shrink-0 p-1 -m-1" aria-label="Pilih untuk dibandingkan" data-testid={`hpp-select-${it.id}`}>
                  {on ? <CheckSquare size={20} className="text-[color:var(--brand)]" /> : <Square size={20} />}
                </button>
                <button onClick={() => nav(`/hpp/${it.id}`)} className="text-left min-w-0 flex-1" data-testid={`hpp-open-${it.id}`}>
                  <div className="font-semibold text-slate-900 truncate">{it.nama}</div>
                  <div className="text-[11px] text-slate-500 truncate">{it.kategori_nama || "Custom"} · margin {fmtPersen(it.margin_persen)}</div>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">HPP Total/Unit</div><div className="font-mono font-semibold">{rupiah(h.hpp_total)}</div></div>
                <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">Harga Jual</div><div className="font-mono font-semibold brand-text">{rupiah(h.harga_aktual)}</div></div>
                <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">Margin</div><div className="font-mono font-semibold">{fmtPersen(h.margin_aktual)}</div></div>
                <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">BEP</div><div className={`font-mono font-semibold ${h.bep_aktual_bulan == null ? "text-rose-600" : "text-emerald-700"}`}>{h.bep_aktual_bulan == null ? "Tidak balik modal" : fmtBulan(h.bep_aktual_bulan)}</div></div>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                <span className="text-[11px] text-slate-400">Diubah {new Date(it.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
                <div className="flex gap-1">
                  <button onClick={() => dup(it)} className="p-2 rounded-sm hover:bg-slate-100 text-slate-600" title="Duplikat" data-testid={`hpp-dup-${it.id}`}><Copy size={16} /></button>
                  <button onClick={() => del(it)} className="p-2 rounded-sm hover:bg-rose-50 text-rose-600" title="Hapus" data-testid={`hpp-del-${it.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
